import { Model, Toast } from 'jamespot-front-business';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { JRCInputTinyMCEProps } from 'jamespot-react-components';
import { FieldValues } from 'react-hook-form';
import { AIModal } from './extension/AIModal';
import { useMarkdownModal } from '../../hooks/useMarkdownModal';
import { MarkdownModal } from './extension/MarkdownModal';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { tinymceCommonOptions } from '../../initTinymce';
import { getTinyMCEMentionsQueries } from '../../utils/tinymce';
import { useAI } from '../../hooks/useAI';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { GifsModal } from './extension/GifsModal';
import { useGifsModal } from '../../hooks/useGifsModal';

const JRCInputTinyMCE = commonComponentsRegistry.getLazyComponent('InputTinyMCE');

export type JRCInputTinyMCEWithExtProps<T extends FieldValues> = Omit<
    JRCInputTinyMCEProps<T>,
    'commonOptions' | 'mentionsQueries' | 'additionalExtensions'
>;

export const JRCInputTinyMCEWithExt = <T extends FieldValues = FieldValues>(props: JRCInputTinyMCEWithExtProps<T>) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const models = useAppSelector(Model.selectors.selectAll);
    const { query, ext: extAI, isActive: isAIActive } = useAI();
    const {
        open,
        onInsert,
        close: closeMarkdownModal,
        ext: extMarkdown,
        isActive: isMarkdownActive,
    } = useMarkdownModal();
    const {
        close: closeGifsModal,
        open: gifsModalOpen,
        isActive: isGifsActive,
        onSelect,
        ext: gifsExt,
    } = useGifsModal();

    const extArray = useMemo(() => {
        return [
            ...(isMarkdownActive ? [extMarkdown] : []),
            ...(isAIActive ? [extAI] : []),
            ...(isGifsActive ? [gifsExt] : []),
        ];
    }, [extAI, extMarkdown, gifsExt, isAIActive, isGifsActive, isMarkdownActive]);

    const onMentionsQueryError = useCallback(
        (message?: string) => {
            dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    const tinyMentionsQueries = useMemo(() => {
        return getTinyMCEMentionsQueries(models, theme, onMentionsQueryError);
    }, [models, onMentionsQueryError, theme]);

    const commonOptions = useMemo(() => tinymceCommonOptions, []);

    return (
        <>
            {commonOptions && (
                <JRCInputTinyMCE
                    commonOptions={commonOptions}
                    additionalExtensions={extArray}
                    mentionsQueries={tinyMentionsQueries}
                    {...props}
                />
            )}
            {isAIActive && <AIModal query={query} />}
            {isMarkdownActive && <MarkdownModal onInsert={onInsert} open={open} close={closeMarkdownModal} />}
            {isGifsActive && <GifsModal onSelect={onSelect} open={gifsModalOpen} close={closeGifsModal} />}
        </>
    );
};
